/**
 * How close, in sixteenths, another character's sampled change in a frame must come to the local character's rendered
 * change for the two to count as moving alike (ADR-032's note of 2026-10-10).
 */
export const FOOTING_MATCH = 0.05;

/**
 * How many frames in a row must call for the other footing before an axis changes to it.
 */
export const FOOTING_FRAMES = 6;

/**
 * One moving axis's footing: whether it is drawn on the local character's footing (its drawn pixel plus the rounded
 * offset from its rendered position) or on its own rounded pixel of the ground, and how many frames in a row have
 * called for the other one. Plain data, one for each axis of each character drawn, written in place every frame.
 */
export interface Footing {
  local: boolean;
  count: number;
}

/**
 * The footing an axis starts on: the ground's, with no count.
 *
 * @returns The footing.
 */
export function startFooting(): Footing {
  return { local: false, count: 0 };
}

/**
 * The footing after a frame in which the axis moved: a frame whose change matches the local character's within
 * `FOOTING_MATCH` calls for the local character's footing and any other for the ground's; one that calls for the
 * footing it is on starts the count again, and the `FOOTING_FRAMES`th in a row that calls for the other changes to
 * it.
 *
 * @param footing The footing before the frame, written in place.
 * @param change The axis's sampled change this frame, in sixteenths.
 * @param localChange The local character's rendered change along the axis this frame, in sixteenths.
 * @returns The same footing, after the frame.
 */
export function footingAfter(footing: Footing, change: number, localChange: number): Footing {
  const alike = Math.abs(change - localChange) <= FOOTING_MATCH;
  if (alike === footing.local) {
    footing.count = 0;
    return footing;
  }
  footing.count += 1;
  if (footing.count >= FOOTING_FRAMES) {
    footing.local = alike;
    footing.count = 0;
  }
  return footing;
}

/**
 * How far past half a pixel, in world pixels, the exact offset between a remote on the local character's footing and
 * the local character must move from the whole offset drawn before it is drawn on another one. Two walkers' sampled
 * rates differ by a fraction of a percent (the render clock steers, `render-clock.ts`), so the exact offset can hover
 * on a half pixel and would round to either side from frame to frame (ADR-032's note of 2026-10-14).
 */
export const OFFSET_HOLD = 0.25;

/**
 * The whole offset to draw a remote at from the local character, in world pixels: the one drawn the frame before while
 * the exact offset stays within half a pixel and `OFFSET_HOLD` of it, otherwise the exact offset rounded.
 *
 * @param before The whole offset drawn the frame before, NaN when the axis was not on the local character's footing.
 * @param exact The exact offset, in world pixels.
 * @returns The whole offset.
 */
export function heldOffset(before: number, exact: number): number {
  if (!Number.isNaN(before) && Math.abs(exact - before) <= 0.5 + OFFSET_HOLD) {
    return before;
  }
  return Math.round(exact);
}
